/**
 * The queue's state vocabulary — in FORM as well as in words, so a scan down
 * the column reads without parsing every label.
 *
 * The tone language is lifted from the engagement Billing tab's `TonePill`
 * rather than imported from it: this console is platform-wide and mostly renders
 * consultation rows, and importing an engagements component would make an AP
 * tool depend on a product feature's chrome. The mapping is deliberately the
 * same one — brand = money moved, purple = working, yellow = out of our hands,
 * grey = terminal bookkeeping, red = needs someone — so an operator who has seen
 * one pill can read the other.
 *
 * BLOCKED is the state this surface adds. A row whose last attempt hit
 * `payouts_not_enabled` cannot be paid until the expert finishes their Stripe
 * setup, and `failureReason` persists that across reloads. It is not `failed`:
 * failed invites a retry, and retrying a blocked row just re-fails against the
 * executor's live pre-flight. Note this marks a row that has BEEN ATTEMPTED —
 * pre-marking an unattempted row needs a per-row Connect read the api
 * deliberately refuses to expose (it is an uncached Stripe round-trip per row);
 * that half is sequenced behind ENG-5633's dataloader.
 */
import { ExpertPaymentStatus } from '@/__generated__/graphql';

interface Tone {
  dot: string;
  text: string;
  bg: string;
  border: string;
}

const TONES: Record<'moved' | 'awaiting' | 'working' | 'quiet' | 'failed', Tone> = {
  moved: {
    dot: 'bg-brand-primary',
    text: 'text-on-surface',
    bg: 'bg-brand-light',
    border: 'border-brand-primary-faint',
  },
  awaiting: {
    dot: 'bg-yellow-500',
    text: 'text-yellow-900 dark:text-yellow-200',
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    border: 'border-yellow-700/40',
  },
  working: {
    dot: 'bg-purple-500',
    text: 'text-purple-900 dark:text-purple-200',
    bg: 'bg-purple-50 dark:bg-purple-900/20',
    border: 'border-purple-700/40',
  },
  quiet: {
    dot: 'bg-on-surface-muted',
    text: 'text-on-surface-muted',
    bg: 'bg-surface-secondary',
    border: 'border-surface-border',
  },
  failed: {
    dot: 'bg-red-500',
    text: 'text-red-900 dark:text-red-200',
    bg: 'bg-red-50 dark:bg-red-900/15',
    border: 'border-red-700/40',
  },
};

const STATUS: Record<ExpertPaymentStatus, { label: string; tone: Tone }> = {
  [ExpertPaymentStatus.Paid]: { label: 'Paid', tone: TONES.moved },
  [ExpertPaymentStatus.Pending]: { label: 'Awaiting release', tone: TONES.awaiting },
  // "Settling", not "Processing": the operator's question about this row is
  // whether the money has landed, and the honest answer is that we do not yet
  // know. The reconciler is working on it hourly.
  [ExpertPaymentStatus.Processing]: { label: 'Settling', tone: TONES.working },
  [ExpertPaymentStatus.Failed]: { label: 'Failed', tone: TONES.failed },
  [ExpertPaymentStatus.Canceled]: { label: 'Canceled', tone: TONES.quiet },
};

/** The one `failureReason` that means "blocked", not "try again". */
export const BLOCKED_REASON = 'payouts_not_enabled';

export function isBlocked(payment: {
  status: ExpertPaymentStatus;
  failureReason?: string | null;
}): boolean {
  return payment.status === ExpertPaymentStatus.Failed && payment.failureReason === BLOCKED_REASON;
}

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }): JSX.Element {
  return (
    <span
      className={`inline-flex w-fit items-center gap-6 rounded-full border px-8 py-2 body-2 ${tone.bg} ${tone.text} ${tone.border}`}
    >
      <span className={`h-6 w-6 shrink-0 rounded-full ${tone.dot}`} aria-hidden />
      {children}
    </span>
  );
}

interface StatusChipProps {
  status: ExpertPaymentStatus;
  failureReason?: string | null;
}

/**
 * A failed row's reason, in words, when we recognise it.
 *
 * `failure_reason` is persisted from whatever refused the transfer, so an
 * insufficient platform balance arrives as Stripe's own `balance_insufficient`
 * string. Rendered raw that reads like a fault with the expert's account rather
 * than with the platform's float — and the two need opposite responses: one is
 * "the expert must finish their setup", the other is "top up the platform
 * balance and retry".
 *
 * Matched on a substring rather than equality because the stored value is
 * truncated provider text, not one of our own codes. Anything unrecognised
 * still renders verbatim — an unfamiliar reason is information, and swallowing
 * it would leave a failed money row with nothing to explain it.
 */
function explainFailure(failureReason: string): string {
  return failureReason.includes('insufficient')
    ? 'The platform Stripe balance could not cover this. Top it up, then retry — nothing was transferred.'
    : failureReason;
}

export default function StatusChip({ status, failureReason }: StatusChipProps): JSX.Element {
  if (isBlocked({ status, failureReason })) {
    return (
      <span className="flex flex-col gap-2">
        <Pill tone={TONES.awaiting}>Not Connect-ready</Pill>
        <span className="text-on-surface-muted body-2">Expert’s payout setup is incomplete</span>
      </span>
    );
  }

  const { label, tone } = STATUS[status];
  return (
    <span className="flex flex-col gap-2">
      <Pill tone={tone}>{label}</Pill>
      {status === ExpertPaymentStatus.Failed && failureReason ? (
        <span className="text-on-surface-muted body-2">{explainFailure(failureReason)}</span>
      ) : null}
    </span>
  );
}
